import { useMemo, useState } from "react";
import MaterialTable from "material-table";
import type { Column } from "material-table";
import {
  Box,
  Button,
  Chip,
  Popover,
  TextField,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import HistoryIcon from "@material-ui/icons/History";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import {
  RowActionsMenu,
  RowActionItem,
} from "../../components/RowActionsMenu/RowActionsMenu";
import type { ReservationListItem, ReservationStatus } from "./types";
import styles from "./ReservationList.module.scss";

// Inline localization to avoid Jest module resolution issues with .js files
const materialTableES = {
  header: { actions: "" },
  body: {
    emptyDataSourceMessage: "",
    deleteTooltip: "Eliminar",
    editTooltip: "Editar",
    editRow: {
      deleteText: "¿Estas seguro que deseas eliminar este registro?",
      saveTooltip: "Guardar",
      cancelTooltip: "Cancelar",
    },
    filterRow: { filterTooltip: "Filtrar" },
  },
  pagination: {
    labelRowsSelect: "Filas",
    labelRowsPerPage: "Filas por página",
    firstAriaLabel: "Primera página",
    firstTooltip: "Primera página",
    previousAriaLabel: "Página anterior",
    previousTooltip: "Página anterior",
    nextAriaLabel: "Siguiente página",
    nextTooltip: "Siguiente página",
    lastAriaLabel: "Última página",
    lastTooltip: "Última página",
    labelDisplayedRows: "{from}-{to} de {count}",
  },
  toolbar: {
    searchTooltip: "Buscar",
    searchPlaceholder: "Buscar",
    nRowsSelected: "{0} productos seleccionados",
  },
};

interface ReservationListProps {
  data: ReservationListItem[];
  isLoading: boolean;
  /** Current user has reservations:approve permission */
  canApprove: boolean;
  /** Current user has reservations:finalize permission */
  canFinalize: boolean;
  /** Current user has reservations:edit_own permission */
  canEditOwn: boolean;
  /** Current user has reservations:view_all permission — controls "Creado por" column visibility */
  canViewAll: boolean;
  /** Numeric user ID of the current user — used to scope edit_own to own reservations */
  currentUserId?: number;
  onView: (reservation: ReservationListItem) => void;
  onEdit: (reservation: ReservationListItem) => void;
  onApprove: (reservation: ReservationListItem, reason?: string) => void;
  onFinalize: (reservation: ReservationListItem, notes?: string) => void;
  onReject: (reservation: ReservationListItem, reason?: string) => void;
  onHistory: (reservation: ReservationListItem) => void;
}

const statusConfig: Record<
  ReservationStatus,
  { label: string; color: "default" | "primary" | "secondary" }
> = {
  pending: { label: "Pendiente", color: "default" },
  approved: { label: "Aprobada", color: "primary" },
  rejected: { label: "Rechazada", color: "secondary" },
  finalized: { label: "Finalizada", color: "primary" },
};

export function getStatusChip(status: ReservationStatus) {
  const config = statusConfig[status] ?? {
    label: status ?? "Desconocido",
    color: "default" as const,
  };
  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
}

interface ConfirmPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  title: string;
  reasonLabel: string;
  reasonRequired?: boolean;
  confirmColor: "primary" | "secondary";
  confirmText: string;
}

function ConfirmPopover({
  anchorEl,
  open,
  onClose,
  onConfirm,
  title,
  reasonLabel,
  reasonRequired = false,
  confirmColor,
  confirmText,
}: ConfirmPopoverProps) {
  const [reason, setReason] = useState("");

  const handleConfirm = () => {
    if (reasonRequired && !reason.trim()) return;
    onConfirm(reason.trim() || undefined);
    setReason("");
  };

  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Box p={2} minWidth={280}>
        <TextField
          label={reasonLabel}
          variant="outlined"
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required={reasonRequired}
          margin="normal"
        />
        <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          <Button size="small" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            color={confirmColor}
            onClick={handleConfirm}
            disabled={reasonRequired && !reason.trim()}
          >
            {confirmText}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}

export function ReservationList({
  data,
  isLoading,
  canApprove,
  canFinalize,
  canEditOwn,
  canViewAll,
  currentUserId,
  onView,
  onEdit,
  onApprove,
  onFinalize,
  onReject,
  onHistory,
}: ReservationListProps) {
  const [approveAnchor, setApproveAnchor] = useState<HTMLElement | null>(null);
  const [rejectAnchor, setRejectAnchor] = useState<HTMLElement | null>(null);
  const [finalizeAnchor, setFinalizeAnchor] = useState<HTMLElement | null>(null);
  const [activeRow, setActiveRow] = useState<ReservationListItem | null>(null);

  const handleApproveClick = (
    triggerEl: HTMLElement,
    row: ReservationListItem
  ) => {
    setApproveAnchor(triggerEl);
    setActiveRow(row);
  };

  const handleRejectClick = (
    triggerEl: HTMLElement,
    row: ReservationListItem
  ) => {
    setRejectAnchor(triggerEl);
    setActiveRow(row);
  };

  const handleFinalizeClick = (
    triggerEl: HTMLElement,
    row: ReservationListItem
  ) => {
    setFinalizeAnchor(triggerEl);
    setActiveRow(row);
  };

  const handleApproveConfirm = (reason?: string) => {
    if (activeRow) onApprove(activeRow, reason);
    setApproveAnchor(null);
    setActiveRow(null);
  };

  const handleRejectConfirm = (reason?: string) => {
    if (activeRow) onReject(activeRow, reason);
    setRejectAnchor(null);
    setActiveRow(null);
  };

  const handleFinalizeConfirm = (notes?: string) => {
    if (activeRow) onFinalize(activeRow, notes);
    setFinalizeAnchor(null);
    setActiveRow(null);
  };

  const columns: Column<ReservationListItem>[] = useMemo(
    () => {
      const cols: Column<ReservationListItem>[] = [
        {
          title: "",
          field: "actions",
          sorting: false,
          cellStyle: { whiteSpace: "nowrap", width: "1%" },
          render: (rowData) => {
            const items: RowActionItem[] = [
              {
                label: "Ver detalle",
                icon: <VisibilityIcon fontSize="small" />,
                onClick: () => onView(rowData),
              },
              {
                label: "Ver historial",
                icon: <HistoryIcon fontSize="small" />,
                onClick: () => onHistory(rowData),
              },
            ];

            if (
              canEditOwn &&
              rowData.status === "pending" &&
              rowData.created_by_user_id === currentUserId
            ) {
              items.push({
                label: "Editar",
                icon: <EditIcon fontSize="small" />,
                onClick: () => onEdit(rowData),
              });
            }

            if (canApprove && rowData.status === "pending") {
              items.push(
                {
                  label: "Aprobar",
                  icon: (
                    <CheckCircleIcon
                      fontSize="small"
                      className={styles.approveIcon}
                    />
                  ),
                  onClick: (triggerEl) =>
                    handleApproveClick(triggerEl, rowData),
                },
                {
                  label: "Rechazar",
                  icon: (
                    <CancelIcon
                      fontSize="small"
                      className={styles.rejectIcon}
                    />
                  ),
                  onClick: (triggerEl) =>
                    handleRejectClick(triggerEl, rowData),
                }
              );
            }

            if (canFinalize && rowData.status === "approved") {
              items.push({
                label: "Finalizar reserva",
                icon: <LocalShippingIcon fontSize="small" />,
                onClick: (triggerEl) =>
                  handleFinalizeClick(triggerEl, rowData),
              });
            }

            return <RowActionsMenu items={items} />;
          },
        },
        {
          title: "Estado",
          field: "status",
          render: (rowData) => getStatusChip(rowData.status),
          cellStyle: { whiteSpace: "nowrap" },
          customFilterAndSearch: (filter, rowData) => {
            const search = filter.toLowerCase();
            const statusLabel = statusConfig[rowData.status]?.label.toLowerCase() || "";
            const statusValue = rowData.status.toLowerCase();
            // Search in both English value and Spanish label
            return statusValue.includes(search) || statusLabel.includes(search);
          },
        },
        { title: "Cotización", field: "quote_number" },
        {
          title: "Cliente",
          field: "client_name",
          render: (rowData) => rowData.client_name || "—",
        },
        { title: "Items", field: "items_count", type: "numeric" },
      ];

      if (canViewAll) {
        cols.push({ title: "Creado por", field: "created_by_name" });
      }

      cols.push(
        {
          title: "Fecha",
          field: "created_at",
          render: (rowData) =>
            new Date(rowData.created_at).toLocaleDateString("es-CO"),
        }
      );

      return cols;
    },
    [
      canApprove,
      canEditOwn,
      canFinalize,
      canViewAll,
      currentUserId,
      onView,
      onEdit,
      onHistory,
    ]
  );

  return (
    <>
      <MaterialTable
        title=""
        columns={columns}
        data={data}
        isLoading={isLoading}
        options={{
          search: true,
          toolbar: true,
          filtering: false,
          paging: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
            height: "3.5rem",
          },
          rowStyle: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#1C252E",
          },
        }}
        localization={materialTableES}
      />

      <ConfirmPopover
        anchorEl={approveAnchor}
        open={Boolean(approveAnchor)}
        onClose={() => {
          setApproveAnchor(null);
          setActiveRow(null);
        }}
        onConfirm={handleApproveConfirm}
        title="Aprobar reserva"
        reasonLabel="Motivo de aprobación (opcional)"
        confirmColor="primary"
        confirmText="Confirmar aprobación"
      />

      <ConfirmPopover
        anchorEl={rejectAnchor}
        open={Boolean(rejectAnchor)}
        onClose={() => {
          setRejectAnchor(null);
          setActiveRow(null);
        }}
        onConfirm={handleRejectConfirm}
        title="Rechazar reserva"
        reasonLabel="Motivo de rechazo"
        reasonRequired
        confirmColor="secondary"
        confirmText="Confirmar rechazo"
      />

      <ConfirmPopover
        anchorEl={finalizeAnchor}
        open={Boolean(finalizeAnchor)}
        onClose={() => {
          setFinalizeAnchor(null);
          setActiveRow(null);
        }}
        onConfirm={handleFinalizeConfirm}
        title="Finalizar reserva"
        reasonLabel="Notas de finalización (opcional)"
        confirmColor="primary"
        confirmText="Confirmar finalización"
      />
    </>
  );
}

export default ReservationList;
